import { z } from 'zod';

/**
 * Mail module (G10, ADR-0019): panel-managed mail hosting on a self-hosted
 * Stalwart Mail Server. The panel is the source of truth — these shapes
 * describe panel entities; the Stalwart wire format lives in the API's
 * stalwart client, never here.
 */

// ── Value vocabulary ────────────────────────────────────────────────

/** RFC 5321 local part, conservatively: what Stalwart's sanitizer keeps. */
export const mailLocalPartSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1)
  .max(64)
  .regex(/^[a-z0-9](?:[a-z0-9._+-]*[a-z0-9])?$/, 'Invalid email local part');

export const mailDomainNameSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(253)
  .regex(
    /^(?=.{3,253}$)[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/,
    'Invalid domain name',
  );

export const MAIL_OBJECT_STATUSES = ['active', 'suspended'] as const;
export type MailObjectStatus = (typeof MAIL_OBJECT_STATUSES)[number];

// ── Settings (admin) ────────────────────────────────────────────────

export const DMARC_POLICIES = ['none', 'quarantine', 'reject'] as const;
export type DmarcPolicy = (typeof DMARC_POLICIES)[number];

export const mailSettingsSchema = z.object({
  baseUrl: z
    .string()
    .trim()
    .url()
    .transform((u) => u.replace(/\/+$/, '')),
  /**
   * Basic-auth username for the management API. Null/absent = bearer
   * auth with an API key (the recommended, least-privileged setup).
   */
  username: z.string().trim().min(1).max(200).nullish(),
  /** API key (bearer) or password (basic). Omit to keep the stored one. */
  secret: z.string().trim().min(1).optional(),
  /**
   * IMAP endpoint of the mail server, for mailbox migrations (the
   * management base URL is HTTP-only). Defaults to the base URL's host.
   */
  imapHost: mailDomainNameSchema.nullish(),
  imapPort: z.number().int().min(1).max(65535).nullish(),
  /**
   * Stalwart principal used for migration IMAP logins: it must hold the
   * `impersonate` permission (composite login `mailbox%user`), so content
   * lands in customer mailboxes without touching their passwords.
   */
  imapMasterUser: z.string().trim().min(1).max(200).nullish(),
  /** Password for imapMasterUser. Omit to keep the stored one. */
  imapMasterSecret: z.string().min(1).optional(),
  /**
   * Default DMARC policy published for domains whose zone file carries no
   * _dmarc record. `none` is the migration-safe minimum the 2026
   * Gmail/Yahoo bulk-sender rules accept; the deliverability check
   * recommends tightening once mail flows are verified.
   */
  dmarcPolicy: z.enum(DMARC_POLICIES).nullish(),
  /** Aggregate-report (rua) address for panel-published DMARC records. */
  dmarcRua: z.string().trim().toLowerCase().email().nullish(),
  /**
   * Resolvers (IPs) used by deliverability checks. Empty/absent = the
   * panel host's system resolver — set public resolvers (1.1.1.1, 8.8.8.8)
   * when the panel host has split-horizon DNS.
   */
  checkResolvers: z.array(z.string().trim().ip()).max(4).nullish(),
  /** Operator-declared usable capacity of the Stalwart mail store. */
  storageCapacityBytes: z.number().int().positive().nullish(),
  /** Capacity kept free during imports; defaults to 10 GiB in the API. */
  storageReserveBytes: z.number().int().nonnegative().nullish(),
});
export type MailSettingsInput = z.infer<typeof mailSettingsSchema>;

export interface MailSettingsDto {
  configured: boolean;
  baseUrl: string | null;
  username: string | null;
  hasSecret: boolean;
  imapHost: string | null;
  imapPort: number | null;
  imapMasterUser: string | null;
  hasImapMasterSecret: boolean;
  dmarcPolicy: DmarcPolicy | null;
  dmarcRua: string | null;
  checkResolvers: string[];
  storageCapacityBytes: number | null;
  storageReserveBytes: number | null;
}

// ── Smarthost (admin; optional outbound relay) ──────────────────────

export const mailSmarthostSchema = z.object({
  /** Relay SMTP server (hostname or IP). */
  address: z.string().trim().min(1).max(253),
  port: z.number().int().min(1).max(65535).default(587),
  /** TLS on connect (465-style) instead of STARTTLS. */
  implicitTls: z.boolean().default(false),
  allowInvalidCerts: z.boolean().default(false),
  username: z.string().trim().min(1).max(200).nullish(),
  /** Relay password/API key; omit to keep the stored one. */
  secret: z.string().min(1).optional(),
  /**
   * SPF mechanism authorizing the relay (e.g. `include:_spf.relay.example`
   * or `ip4:203.0.113.9`) — appended to every domain's published SPF while
   * the smarthost is active.
   */
  spfMechanism: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^(include:[a-z0-9._-]+|ip4:[0-9./]+|ip6:[0-9a-f:/]+|a:[a-z0-9._-]+|mx:[a-z0-9._-]+)$/, {
      message: 'Must be a single SPF mechanism (include:, ip4:, ip6:, a: or mx:)',
    })
    .nullish(),
});
export type MailSmarthostInput = z.infer<typeof mailSmarthostSchema>;

export interface MailSmarthostDto {
  configured: boolean;
  address: string | null;
  port: number | null;
  implicitTls: boolean;
  allowInvalidCerts: boolean;
  username: string | null;
  hasSecret: boolean;
  spfMechanism: string | null;
  /** Stalwart MtaRoute id once applied upstream. */
  routeId: string | null;
}

export interface MailTestResultDto {
  ok: boolean;
  /** Server edition as reported by GET /api/account (oss|community|enterprise). */
  edition: string | null;
  error: string | null;
}

// ── Domains ─────────────────────────────────────────────────────────

export const createMailDomainSchema = z.object({
  domain: mailDomainNameSchema,
  catchAllAddress: z.string().trim().toLowerCase().email().nullish(),
});
export type CreateMailDomainInput = z.infer<typeof createMailDomainSchema>;

/** Customers select an assigned zone; provider-only fields are rejected. */
export const createCustomerMailDomainSchema = createMailDomainSchema
  .pick({ domain: true })
  .strict();
export const confirmMailDomainSchema = z.object({ confirmDomain: mailDomainNameSchema }).strict();
export const publishMailDomainDnsSchema = confirmMailDomainSchema.extend({
  replaceNoMailRecords: z.boolean().default(false),
});

export const updateMailDomainSchema = z.object({
  catchAllAddress: z.string().trim().toLowerCase().email().nullable(),
});
export type UpdateMailDomainInput = z.infer<typeof updateMailDomainSchema>;

export interface MailDomainDto {
  id: string;
  subscriptionId: string;
  domain: string;
  stalwartId: string | null;
  status: MailObjectStatus;
  catchAllAddress: string | null;
  /** False while migration-created destination records are deliberately staged. */
  dnsPublicationEnabled: boolean;
  orphanedAt: string | null;
  createdAt: string;
  mailboxCount: number;
  forwarderCount: number;
}

/** The DNS records Stalwart wants published, straight from the server. */
export interface MailDomainDnsDto {
  domain: string;
  zoneFile: string | null;
  /** Set when the live fetch failed; zoneFile is then the cached copy. */
  liveError: string | null;
}

// ── Mailboxes ───────────────────────────────────────────────────────

export const mailboxPasswordSchema = z.string().min(10).max(256);

export const createMailboxSchema = z.object({
  localPart: mailLocalPartSchema,
  password: mailboxPasswordSchema,
  displayName: z.string().trim().max(200).nullish(),
  /** Per-mailbox disk quota in MB; null = unlimited (plan may forbid). */
  quotaMb: z.number().int().min(1).nullable().default(null),
});
export type CreateMailboxInput = z.infer<typeof createMailboxSchema>;

export const updateMailboxSchema = z.object({
  password: mailboxPasswordSchema.optional(),
  displayName: z.string().trim().max(200).nullable().optional(),
  quotaMb: z.number().int().min(1).nullable().optional(),
});
export type UpdateMailboxInput = z.infer<typeof updateMailboxSchema>;

export interface MailboxDto {
  id: string;
  domainId: string;
  address: string;
  localPart: string;
  displayName: string | null;
  quotaMb: number | null;
  /** Disk usage reported by Stalwart at the last reconcile, in bytes. */
  usedBytes: number | null;
  usageCheckedAt?: string | null;
  usageRefreshFailed?: boolean;
  loginEnabled?: boolean;
  deliveryEnabled?: boolean;
  accessPending?: boolean;
  status: MailObjectStatus;
  stalwartId: string | null;
  orphanedAt: string | null;
  createdAt: string;
  aliases: MailAliasDto[];
}

export const mailboxAccessSchema = z
  .object({
    loginEnabled: z.boolean(),
    deliveryEnabled: z.boolean(),
    confirmAddress: z.string().trim().toLowerCase().email().optional(),
  })
  .strict();
export const deleteMailboxSchema = z
  .object({ confirmAddress: z.string().trim().toLowerCase().email() })
  .strict();
export const patchMailSettingsSchema = mailSettingsSchema.partial().strict();
export const mailInventoryQuerySchema = z
  .object({
    search: z.string().trim().max(200).default(''),
    subscriptionId: z.string().uuid().optional(),
    domainId: z.string().uuid().optional(),
    clientId: z.string().uuid().optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
  })
  .strict();
export interface MailCapability {
  allowed: boolean;
  reason: string | null;
}
export interface MailWorkspaceMailboxDto extends Omit<MailboxDto, 'stalwartId'> {
  setupStatus: 'ready' | 'needs-attention';
  domain: string;
  subscriptionId: string;
  capabilities: { manage: MailCapability; delete: MailCapability; preferences: MailCapability };
}
export interface MailWorkspaceDomainDto {
  id: string;
  domain: string;
  subscriptionId: string;
  subscriptionName: string;
  clientId: string | null;
  clientName: string | null;
  status: MailObjectStatus;
  setupStatus: 'ready' | 'needs-attention';
  publicationStatus: 'enabled' | 'provider-review';
  catchAllAddress: string | null;
  quotaCeilingMb: number | null;
  knownUsedBytes: number;
  unknownUsageCount: number;
  mailboxes: MailWorkspaceMailboxDto[];
  forwarders: Omit<MailForwarderDto, 'stalwartId'>[];
  capabilities: {
    manage: MailCapability;
    preferences: MailCapability;
    delete: MailCapability;
    publishDns: MailCapability;
    retrySetup: MailCapability;
  };
}
export interface MailInventoryDto<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ── Aliases (extra addresses delivering into a mailbox) ────────────

export const createMailAliasSchema = z.object({
  localPart: mailLocalPartSchema,
  /** Alias domain; defaults to the mailbox's own domain. */
  domainId: z.string().uuid().optional(),
});
export type CreateMailAliasInput = z.infer<typeof createMailAliasSchema>;

export interface MailAliasDto {
  id: string;
  mailboxId: string;
  domainId: string;
  address: string;
  localPart: string;
  createdAt: string;
}

// ── Forwarders (address → external/internal recipients) ────────────

export const createMailForwarderSchema = z.object({
  localPart: mailLocalPartSchema,
  recipients: z.array(z.string().trim().toLowerCase().email()).min(1).max(50),
});
export type CreateMailForwarderInput = z.infer<typeof createMailForwarderSchema>;

export const updateMailForwarderSchema = z.object({
  recipients: z.array(z.string().trim().toLowerCase().email()).min(1).max(50),
});
export type UpdateMailForwarderInput = z.infer<typeof updateMailForwarderSchema>;

export interface MailForwarderDto {
  id: string;
  domainId: string;
  address: string;
  localPart: string;
  recipients: string[];
  stalwartId: string | null;
  orphanedAt: string | null;
  createdAt: string;
}

// ── Per-subscription rollup ─────────────────────────────────────────

export interface SubscriptionMailDomainDto extends MailDomainDto {
  canPublishDns?: boolean;
  mailboxes: MailboxDto[];
  forwarders: MailForwarderDto[];
  /** Last deliverability check rollup (G13); status `unknown` = never ran. */
  deliverability: MailDeliverabilitySummaryDto;
}

export interface SubscriptionMailDto {
  subscriptionId: string;
  featureEnabled: boolean;
  configured: boolean;
  domains: SubscriptionMailDomainDto[];
}

export const webmailSettingsSchema = z
  .object({
    secret: z
      .string()
      .regex(
        /^[A-Za-z0-9_-]{32,128}$/,
        'Use a randomly generated integration secret of 32–128 characters',
      ),
  })
  .strict();
export const updateSubscriptionWebmailSchema = z
  .object({
    subscriptionId: z.string().uuid(),
    enabled: z.boolean(),
  })
  .strict();
export const webmailAccessSchema = z.object({ domain: mailDomainNameSchema }).strict();
export interface WebmailSettingsDto {
  configured: boolean;
}
export interface WebmailAccessDto {
  allowed: boolean;
}
export interface SubscriptionWebmailDto {
  subscriptionId: string;
  enabled: boolean;
  configured: boolean;
}

// ── Module status & reconciliation ──────────────────────────────────

export interface MailReconcileStats {
  domainsChecked: number;
  mailboxesChecked: number;
  forwardersChecked: number;
  orphaned: number;
  relinked: number;
  usageUpdated: number;
  suspensionRepaired: number;
  /** Stalwart domains that no panel row claims (never auto-deleted). */
  unmanagedDomains: string[];
}

export interface MailReconcileRunDto {
  startedAt: string;
  finishedAt: string | null;
  durationMs: number | null;
  status: 'success' | 'failed';
  error: string | null;
  stats: MailReconcileStats | null;
}

export interface MailStatusDto {
  configured: boolean;
  featureKey: string;
  running: boolean;
  lastReconcile: MailReconcileRunDto | null;
  domainCount: number;
  mailboxCount: number;
}

// ── G9 annotation attach (Plesk mail limits → plans) ───────────────

export interface MailAttachPlanResult {
  entityType: 'plan' | 'subscription';
  entityId: string;
  name: string;
  /** Panel limit key → applied value (null = unlimited). */
  applied: Record<string, number | null>;
  /** Raw Plesk keys that could not be interpreted. */
  skipped: string[];
}

export interface MailAttachResultDto {
  results: MailAttachPlanResult[];
  /** External refs with mail annotations whose panel entity is gone. */
  dangling: number;
}

// ── Deliverability (G13) ────────────────────────────────────────────

/**
 * Record types a mail domain may need. TLSA (DANE) is publishable by the
 * DNS module when its structured value passes the shared strict validator.
 */
export const MAIL_DNS_RECORD_TYPES = [
  'MX',
  'TXT',
  'CNAME',
  'SRV',
  'A',
  'AAAA',
  'CAA',
  'TLSA',
] as const;
export type MailDnsRecordType = (typeof MAIL_DNS_RECORD_TYPES)[number];

export const MAIL_DNS_PURPOSES = [
  'mx',
  'spf',
  'dkim',
  'dmarc',
  'autoconfig',
  'autodiscover',
  'mta-sts',
  'dane',
  'other',
] as const;
export type MailDnsPurpose = (typeof MAIL_DNS_PURPOSES)[number];

/** One DNS record a mail domain should publish. */
export interface MailDnsRecordDto {
  /** Owner FQDN, no trailing dot (e.g. `mail._domainkey.example.com`). */
  name: string;
  type: MailDnsRecordType;
  ttl: number;
  /** Presentation-format rdata (targets carry their trailing dot). */
  value: string;
  purpose: MailDnsPurpose;
  /** False when the DNS module cannot safely represent this exact value. */
  panelSupported: boolean;
}

/** A DKIM signing key as cached from Stalwart's DkimSignature objects. */
export interface MailDkimKeyDto {
  id: string;
  selector: string;
  /** Wire variant, e.g. `Dkim1Ed25519Sha256`. */
  algorithm: string;
  /** pending | active | retiring | retired (server lifecycle). */
  stage: string;
  nextTransitionAt: string | null;
  createdAt: string | null;
  /** Server-derived public key (PEM) — what the DNS TXT record publishes. */
  publicKeyPem: string | null;
}

export const MAIL_CHECK_STATUSES = ['pass', 'warn', 'fail', 'unknown', 'skipped'] as const;
export type MailCheckStatus = (typeof MAIL_CHECK_STATUSES)[number];

export interface MailDeliverabilityCheckDto {
  /** mx | spf | dkim | dmarc | fcrdns | autoconfig | bulk_sender */
  key: string;
  label: string;
  status: MailCheckStatus;
  /** What the resolver actually returned (null = lookup not performed). */
  observed: string[] | null;
  /** What the check wanted to see. */
  expected: string | null;
  detail: string;
}

export interface MailDeliverabilitySummaryDto {
  status: MailCheckStatus;
  pass: number;
  warn: number;
  fail: number;
  checkedAt: string | null;
}

export interface MailDeliverabilityDto {
  domain: string;
  /** Every record the domain should publish. */
  records: MailDnsRecordDto[];
  /**
   * The panel DNS zone hosting this domain (same subscription), if any —
   * records are then auto-published and the checklist is informational.
   */
  zone: { id: string; name: string } | null;
  dkim: MailDkimKeyDto[];
  checks: MailDeliverabilityCheckDto[];
  summary: MailDeliverabilitySummaryDto;
  /** Set when live Stalwart/DNS reads failed and cached data is shown. */
  liveError: string | null;
}

// ── Mailbox migration (G13) ─────────────────────────────────────────

export const MAIL_MIGRATION_STATUSES = [
  'planned',
  'pending',
  'running',
  'waiting',
  'completed',
  'failed',
  'cancelled',
] as const;
export type MailMigrationStatus = (typeof MAIL_MIGRATION_STATUSES)[number];

export const MAIL_MIGRATION_STEPS = [
  'provision',
  'preflight',
  'sync',
  'verify',
  'activate_rules',
  'final_sync',
  'cutover',
  'catch_up',
  'complete',
] as const;
export type MailMigrationStep = (typeof MAIL_MIGRATION_STEPS)[number];

export const mailMigrationAcceptanceSchema = z.object({
  passwordsVerified: z.boolean(),
  webmailVerified: z.boolean(),
  deliveryVerified: z.boolean(),
  /** Required for completion; absent legacy evidence is not acceptance. */
  clientFoldersVerified: z.boolean().optional(),
  /** Operator evidence from source delivery logs, never inferred from DNS alone. */
  sourceQuietSince: z.string().datetime().nullable(),
});
export type MailMigrationAcceptance = z.infer<typeof mailMigrationAcceptanceSchema>;

export const MAIL_MIGRATION_PHASES = ['initial', 'cutover'] as const;
export type MailMigrationPhase = (typeof MAIL_MIGRATION_PHASES)[number];

export const MAIL_MIGRATION_ITEM_STATES = [
  'pending',
  'provisioned',
  'syncing',
  'synced',
  'verified',
  'failed',
  'skipped',
] as const;
export type MailMigrationItemState = (typeof MAIL_MIGRATION_ITEM_STATES)[number];

/**
 * Source-IMAP authentication. `master` uses a Dovecot master user
 * (login `user*master` with the master password — one credential for the
 * whole box, nothing stored per mailbox); `per_mailbox` takes explicit
 * per-address passwords pasted by the operator. `ssh` uses the configured
 * Plesk SSH key and a verified host-key pin for administrative read-only IMAP.
 */
export const MAIL_MIGRATION_AUTH_MODES = ['master', 'per_mailbox', 'ssh'] as const;
export type MailMigrationAuthMode = (typeof MAIL_MIGRATION_AUTH_MODES)[number];

export const startMailMigrationSchema = z.object({
  /** Source IMAP host; defaults to the Plesk connection's host. */
  sourceHost: z.string().trim().min(1).max(253).optional(),
  /** Stable source connection address; TLS still verifies sourceHost. */
  sourceAddress: z.string().trim().ip().optional(),
  sourcePort: z.number().int().min(1).max(65535).default(993),
  sourceTls: z.enum(['ssl', 'starttls', 'none']).default('ssl'),
  authMode: z.enum(MAIL_MIGRATION_AUTH_MODES),
  /** Operator-verified SHA256 SSH host-key digest, lowercase hex; required for SSH mode. */
  sourceSshHostKeySha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/)
    .optional(),
  /** Dovecot master user name (authMode `master`). */
  masterUser: z.string().trim().min(1).max(200).optional(),
  /** Master password (authMode `master`). Stored encrypted per migration. */
  masterPassword: z.string().min(1).optional(),
  /** address → password (authMode `per_mailbox`). Stored encrypted. */
  passwords: z.record(z.string(), z.string().min(1)).optional(),
  /** Subset of source addresses to migrate; absent = all in inventory. */
  addresses: z.array(z.string().trim().toLowerCase().email()).min(1).optional(),
  /** Explicit supported rule subset; absent selects every plan rule marked selected. */
  selectedRuleKeys: z.array(z.string().trim().min(1).max(500)).max(500).optional(),
  /** imapsync --maxbytespersecond (per mailbox), null = unthrottled. */
  maxBytesPerSecond: z.number().int().min(1024).nullish(),
  /** imapsync --maxmessagespersecond, null = unthrottled. */
  maxMessagesPerSecond: z.number().int().min(1).nullish(),
  /** Mailboxes synced in parallel. */
  concurrency: z.number().int().min(1).max(3).default(1),
});
export type StartMailMigrationInput = z.infer<typeof startMailMigrationSchema>;

/** Non-secret half of a migration's configuration, as persisted. */
export interface MailMigrationConfigDto {
  sourceHost: string;
  sourceAddress?: string;
  sourcePort: number;
  sourceTls: 'ssl' | 'starttls' | 'none';
  authMode: MailMigrationAuthMode;
  sourceSshHostKeySha256?: string;
  masterUser: string | null;
  maxBytesPerSecond: number | null;
  maxMessagesPerSecond: number | null;
  concurrency: number;
}

export const mailMigrationSourceSchema = z.object({
  sourceAddress: z.string().trim().ip(),
});

export function mailMigrationSourceIsPinned(config: MailMigrationConfigDto): boolean {
  return z
    .string()
    .ip()
    .safeParse(config.sourceAddress ?? config.sourceHost).success;
}

/** What the engine plans to do with one inventory mailbox. */
export const MAIL_MIGRATION_PLAN_ACTIONS = [
  'create',
  'exists',
  'forwarder',
  'unsupported',
] as const;
export type MailMigrationPlanAction = (typeof MAIL_MIGRATION_PLAN_ACTIONS)[number];

export interface MailMigrationPlanItemDto {
  address: string;
  domain: string;
  action: MailMigrationPlanAction;
  quotaBytes: number | null;
  usedBytes: number | null;
  aliases: string[];
  forwardingAddresses: string[];
  rules: MailMigrationRulePlanDto[];
  warnings: string[];
}

export const MAIL_MIGRATION_RULE_TYPES = [
  'alias',
  'forward',
  'autoresponder',
  'catch_all',
  'spam',
  'virus',
] as const;
export type MailMigrationRuleType = (typeof MAIL_MIGRATION_RULE_TYPES)[number];

export const MAIL_MIGRATION_RULE_DISPOSITIONS = [
  'create',
  'exists',
  'manual',
  'unsupported',
] as const;
export type MailMigrationRuleDisposition = (typeof MAIL_MIGRATION_RULE_DISPOSITIONS)[number];

export interface MailMigrationRulePlanDto {
  key: string;
  type: MailMigrationRuleType;
  address: string | null;
  disposition: MailMigrationRuleDisposition;
  /** Supported rules are selected by default; manual/unsupported rules require an explicit resolution. */
  selected: boolean;
  source: Record<string, unknown>;
  desired: Record<string, unknown> | null;
  detail: string;
}

export interface MailMigrationPlanDto {
  subscriptionId: string;
  /** Snapshot the inventory came from. */
  snapshotId: string | null;
  snapshotAt: string | null;
  items: MailMigrationPlanItemDto[];
  domainRules: MailMigrationRulePlanDto[];
  /** Mail domains that would be created on the panel + Stalwart. */
  domainsToCreate: string[];
  warnings: string[];
  /** imapsync availability on the panel host (null = probe failed). */
  imapsyncVersion: string | null;
}

export interface MailMigrationItemDto {
  id: string;
  address: string;
  domain: string;
  state: MailMigrationItemState;
  attempts: number;
  lastError: string | null;
  sourceMessages: number | null;
  sourceBytes: number | null;
  destMessages: number | null;
  destBytes: number | null;
  transferredMessages: number | null;
  transferredBytes: number | null;
  startedAt: string | null;
  finishedAt: string | null;
}

export const MAIL_MIGRATION_RULE_STATES = [
  'pending',
  'active',
  'verified',
  'failed',
  'manual',
  'unsupported',
] as const;
export type MailMigrationRuleState = (typeof MAIL_MIGRATION_RULE_STATES)[number];

export interface MailMigrationRuleReviewEvidenceDto {
  replacesRuleId: string;
  snapshotId: string;
  digest: string;
  reviewedAt: string;
}
export interface MailMigrationRuleReviewDto {
  digest: string;
  snapshotId: string | null;
  snapshotAt: string | null;
  current: MailMigrationRuleDto;
  proposed: MailMigrationRulePlanDto | null;
  blockers: string[];
}
export const approveMailRuleReviewSchema = z
  .object({ digest: z.string().regex(/^[a-f0-9]{64}$/) })
  .strict();

export interface MailMigrationRuleDto extends MailMigrationRulePlanDto {
  review?: MailMigrationRuleReviewEvidenceDto;
  id: string;
  dnsPublicationEnabled: boolean;
  state: MailMigrationRuleState;
  attempts: number;
  equality: boolean | null;
  lastError: string | null;
  checkedAt: string | null;
}

export interface MailMigrationPreflightCheckDto {
  key:
    | 'quota'
    | 'storage'
    | 'dns'
    | 'ptr'
    | 'spf'
    | 'dkim'
    | 'dmarc'
    | 'tls'
    | 'credentials'
    | 'spam'
    | 'virus';
  status: 'pass' | 'warn' | 'fail';
  detail: string;
}

export interface MailMigrationDomainPreflightDto {
  domain: string;
  checks: MailMigrationPreflightCheckDto[];
  ready: boolean;
}

/** Exact panel-owned metadata retained for reviewed MX replacement and rollback. */
export interface MailMigrationMxRecordDto {
  id: string;
  name: string;
  type: 'MX';
  ttl: number;
  content: string;
  origin: 'user';
  templateKey: string | null;
  autoKey: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface MailMigrationMxChangeDto {
  /** Provider identity/target pin; publication may be paused for rollback. */
  providerBindingDigest: string;
  zoneId: string;
  zoneName: string;
  name: string;
  before: MailMigrationMxRecordDto[];
  after: MailMigrationMxRecordDto[];
}
export interface MailMigrationMxPlanDto {
  /** Approved migration-specific MX hostname; absent/null uses the server default. */
  mxTarget?: string | null;
  digest: string;
  scopeDigest: string;
  changes: MailMigrationMxChangeDto[];
  blockers: string[];
}
export interface MailMigrationMxCutoverDto {
  plan: MailMigrationMxPlanDto;
  appliedAt: string | null;
}
const migrationMxTargetSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((value) => value.replace(/\.$/, ''))
  .pipe(mailDomainNameSchema)
  .refine((value) => !/^[0-9.]+$/.test(value), 'Use a hostname, not an IP address');
export const previewMailMigrationMxSchema = z
  .object({ mxTarget: migrationMxTargetSchema.nullable().optional() })
  .strict();
export const approveMailMigrationMxSchema = previewMailMigrationMxSchema
  .extend({ digest: z.string().regex(/^[a-f0-9]{64}$/) })
  .strict();

export interface MailMigrationDto {
  id: string;
  subscriptionId: string;
  snapshotId: string | null;
  phase: MailMigrationPhase;
  status: MailMigrationStatus;
  requestedStep: MailMigrationStep | null;
  completedSteps: MailMigrationStep[];
  preflight: MailMigrationDomainPreflightDto[];
  mxCutover?: MailMigrationMxCutoverDto | null;
  config: MailMigrationConfigDto;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
  cutoverAt: string | null;
  lastCatchupAt: string | null;
  lastSourceChangeAt: string | null;
  acceptance: MailMigrationAcceptance | null;
  items: MailMigrationItemDto[];
  rules: MailMigrationRuleDto[];
}

export interface SubscriptionMailMigrationDto {
  subscriptionId: string;
  /** Most recent migration first. */
  migrations: MailMigrationDto[];
  running: boolean;
}

// Customer/provider mailbox preferences; script identifiers remain API-internal.
export const MAIL_PREFERENCES_FEATURE = 'mail_manage_preferences';
export const mailboxPreferencesSchema = z
  .object({
    vacationEnabled: z.boolean(),
    /** UTC instants at whole-second precision; omitted/null bounds are open. */
    startsAt: z.string().datetime({ precision: 0 }).nullable().optional(),
    endsAt: z.string().datetime({ precision: 0 }).nullable().optional(),
    subject: z
      .string()
      .trim()
      .max(200)
      .regex(/^[^\r\n\x00]*$/),
    message: z
      .string()
      .max(20000)
      .refine((s) => !s.includes('\0'), 'Invalid reply text'),
    format: z.enum(['text', 'html']),
    junkEnabled: z.boolean(),
  })
  .strict()
  .refine((v) => !v.vacationEnabled || v.message.trim().length > 0, {
    message: 'A reply message is required when vacation replies are enabled',
    path: ['message'],
  })
  .refine((v) => !v.startsAt || !v.endsAt || v.startsAt < v.endsAt, {
    message: 'The end time must be after the start time',
    path: ['endsAt'],
  });
export type MailboxPreferencesInput = z.infer<typeof mailboxPreferencesSchema>;
export interface MailboxPreferencesDto {
  mailboxId: string;
  preferences: MailboxPreferencesInput;
  state: 'ready' | 'pending' | 'external';
  explanation: string | null;
}
